/**
 * Data-layer adapters.
 *
 * Every feature package ships its UI with an INJECTED backend port (CatalogApi,
 * TeamsApi, AlertsSource, SearchProvider, NotificationSource, SavedViewsSource).
 * The dashboard fulfils them all with the shared `@authe/fe-commons` `ApiClient`
 * via the `createApi*` adapters, so there is exactly ONE HTTP brain (region
 * routing, failover, auth, envelope unwrap) behind the whole app — no per-feature
 * fetch code.
 *
 * These are hooks (not module singletons) because the `ApiClient` comes from
 * `<ApiProvider>` context; each is memoised on the client so the adapter is
 * stable across renders.
 */
import * as React from "react";
import { useApiClient, useOptionalResourceChanges } from "@authe/ui-core/api";
import { useSession } from "@authe/ui-core/session";
import { usePermissions } from "@authe/ui-core/permissions";
import type { AutheEvent, EventPresenterContext } from "@authe/fe-commons/events";
import {
  createApiCatalogApi,
  withCatalogChanges,
  createCatalogLogoAdapter,
  type CatalogApi,
  type CatalogSubscribe,
} from "@authe/catalog";
import { createApiTeamsApi, type TeamsApi } from "@authe/teams";
import { createApiSearchProvider } from "@authe/search";
import {
  createApiNotificationSource,
  mergeNotificationSources,
} from "@authe/notification-center";
import { subscribeDescriptor, useOptionalStreamingClient, streamChannelIdsForCaller, STREAM_NOTIFICATIONS } from "@authe/streaming";
import { useTeamIds } from "@/app/useTeamRoster";
import { toNotificationEvent } from "./notificationEvent";
import { createCatalogSubscribe } from "./catalogLive";
import { createApiSavedViewsSource } from "@authe/saved-views";
import { createApiExportLedger } from "@authe/export";
import { createApiInvestigationsSource } from "./investigationsSource";
import { createApiPipelinesSource } from "./pipelinesSource";
import { createApiTracersSource } from "./tracersSource";
import { createApiRunsSource } from "./runsSource";
import { createApiReviewsSource } from "./reviewsSource";
import { createUsageApi, type UsageApi } from "@authe/fe-commons/plans";
import { createAuditSource, type AuditSource } from "./auditSource";
import { createApiReplaysSource } from "./replaysSource";
import { createApiGroupingRulesSource } from "@authe/fe-commons/grouping-rules";
import { createApiRelatedAddressesSource } from "./relatedAddressesSource";
import { createApiPipelineAlertsSource } from "./alertsSource";
import { createIntegrationsSource } from "./integrationsSource";
import { createApiTenancySource } from "./tenancySource";
import { createApiOrgUsersSource } from "./orgUsersSource";
import { createApiAnalyticsSource } from "./analyticsSource";
import { createApiExplorerSource } from "./explorerSource";
import { createApiEvidencePolicySource } from "./evidencePolicySource";
import { createApiProtocolsSource } from "./protocolsSource";
import { createApiFeaturesSource } from "./featuresSource";
import { createApiPoliciesSource } from "./policiesSource";
import {
  createApiAbiByAddressClient,
  createApiAbiProvider,
  createApiAbiWriter,
  createApiContractSourceClient,
  createApiContractStateClient,
  createApiProxyBatchResolver,
  createApiProxyResolver,
  createCompositeAbiProvider,
  createDefaultAbiProvider,
} from "@authe/contract-explorer";
import { createProxyOverridesClient } from "@authe/fe-commons/contracts";
import { createAddressesApi, type Address } from "@authe/fe-commons/addresses";
import { useFeatureFlags } from "@authe/ui-core/flags";
import { useTransportCapabilities } from "@authe/ui-core/api";
import { useOptionalGraphqlClient } from "@/app/graphql";
import { withGraphqlCatalogList } from "./graphqlCatalog";
import { withGraphqlAddresses } from "./graphqlAddresses";
import { restVisibility } from "@authe/fe-commons/attribution";
import {
  appConfig,
  RESOURCES,
  type AttributionResource,
  type AttributionScope,
} from "@/config";
import { useScopePrefs } from "@/app/scopePrefs";
import { useCurrentUserId } from "@/app/storageIdentity";
import { useMyTeams } from "@/app/useMyTeams";
import { useQueryClient } from "@tanstack/react-query";
import type { AddressScope } from "@authe/fe-commons/graphql";
import {
  catalogBlockToResolvedOverlays,
  type AddressCatalogBlock,
  type BatchEntry,
  type BatchOverlays,
  type OverlayRef,
} from "@authe/fe-commons/overlays";
import { addressOverlaysBatchQueryKey } from "@/features/overlays/useResolvedAddressOverlaysBatch";

/**
 * Wrap `CatalogApi.create` so an address-bound row (a label/tag/group/entity
 * created WITH an `address` + `chain`) is auto-attached to that address by the
 * backend at the caller's default visibility. We inject the ABAC carriers
 * (`user_scoped` / `visible_to_team_ids`) derived from the caller's effective
 * default write scope for that kind into the create body; `catalog-mgmt`'s
 * `_create_item` pops them off before persisting the row and uses them for the
 * `attach_overlay` it fires. Keeping the scope here (not hardcoded server-side)
 * is what makes the default a single, now user-editable, knob. The `address`
 * kind is the address row itself (not an overlay on an address), so it's never
 * auto-attached.
 *
 * The carriers come from the same `restVisibility` mapper an explicit attach
 * uses, which is the point: this path used to build them itself and pass no
 * team ids, so creating an address-bound row with "team" configured produced a
 * private attach the caller's team could not see. A scope means one thing on
 * the wire, defined once.
 *
 * One honest limit: there is no case context down here, so the shared-case
 * raise `useAttributionController` applies does not reach this path. Creating a
 * row inside a shared case still auto-attaches at the caller's own default.
 */
function withCatalogAutoAttachScope(
  api: CatalogApi,
  resolveDefaultWriteScope: (resource: AttributionResource) => AttributionScope,
  teamIds: string[],
): CatalogApi {
  return {
    ...api,
    create(kind, body) {
      const address = body?.address;
      const chain = body?.chain;
      const addressBound =
        kind !== "address" &&
        typeof address === "string" &&
        address !== "" &&
        typeof chain === "string" &&
        chain !== "";
      if (!addressBound) return api.create(kind, body);
      const scope = resolveDefaultWriteScope(kind as AttributionResource);
      return api.create(kind, { ...body, ...restVisibility({ scope, teamIds }) });
    },
  };
}

/**
 * The catalog port. Reads (`list`) take the fast GraphQL path when the transport
 * registry enables it (`graphql.enabled` + `graphql.catalog.list`), falling back
 * to REST on any GraphQL error; writes + logos always go through REST. When no
 * GraphQL client is configured the wrapper is a pure pass-through.
 */
export function useCatalogApi() {
  const changes = useOptionalResourceChanges();
  const client = useApiClient();
  const graphqlClient = useOptionalGraphqlClient();
  const flags = useFeatureFlags();
  // The plan ceiling. These wrappers are not components, so they cannot read it
  // themselves — and a wrapper that resolves transport without it would route a
  // tier onto a path its plan withholds.
  const capabilities = useTransportCapabilities();
  const { resolveDefaultWriteScope } = useScopePrefs();
  const teams = useMyTeams();
  const teamIds = React.useMemo(() => teams.map((t) => t.id), [teams]);
  return React.useMemo(() => {
    // Catalog rows live in catalog-mgmt; logos go through the presigned-asset
    // flow (not a JSON POST), so wire the host's logo adapter (S3 upload ticket
    // + CDN preview) onto the otherwise-network-free CatalogApi.
    const logo = createCatalogLogoAdapter({ client, cdnBase: appConfig.cdnUrl });
    const rest = createApiCatalogApi(client, {
      uploadLogo: logo.uploadLogo,
      resolveLogoUrl: logo.resolveLogoUrl,
    });
    return withCatalogAutoAttachScope(
      withCatalogChanges(withGraphqlCatalogList(rest, { graphqlClient, flags, capabilities }), changes),
      resolveDefaultWriteScope,
      teamIds,
    );
  }, [client, graphqlClient, flags, capabilities, resolveDefaultWriteScope, teamIds, changes]);
}

/**
 * Live catalog changes for `<CatalogManager subscribe={…}>`, or `null` when the
 * app has no streaming transport (Live mode off).
 *
 * The catalog is shared reference data, so a list is stale the moment a
 * teammate — or the pipeline — touches a row. Wiring the feed the backend has
 * been publishing all along is what makes an open list stay true: a rename or a
 * retirement lands in place, and a new row is offered rather than left
 * invisible until someone reloads.
 */
export function useCatalogSubscribe(): CatalogSubscribe | null {
  const stream = useOptionalStreamingClient();
  const { session } = useSession();
  const customerId = session?.tenant.customerId ?? null;
  const teamIds = useTeamIds();
  const teamKey = JSON.stringify(teamIds);
  const orgId = session?.tenant.orgId ?? null;
  const tenantId = session?.tenant.tenantId ?? null;
  const userId = session?.user.id ?? null;
  return React.useMemo(
    () => createCatalogSubscribe(stream, { customerId, orgId, tenantId, userId }, JSON.parse(teamKey) as string[]),
    [stream, customerId, orgId, tenantId, userId, teamKey],
  );
}

/**
 * The typed `address-mgmt-api` client (`@authe/fe-commons/addresses`) over the
 * shared `ApiClient`. Speaks canonical `/addresses/...` paths; `RESOURCE_REWRITES`
 * maps them to the private tier (see the address-mgmt-api routing gaps ADDR1/ADDR2
 * in `front-ends/docs/backend-prereqs.md`).
 *
 * The IDEMPOTENT overlay/membership ops (assignment attach/detach + list,
 * primary set/clear, entity-mapping get/put/delete) take the fast GraphQL path
 * when the transport registry enables them (`graphql.enabled` + the per-op flag,
 * e.g. `graphql.address.assignments.write`), falling back to REST on any GraphQL
 * error; every non-idempotent write stays on REST. With no GraphQL client the
 * wrapper is a pure pass-through.
 *
 * `list` over GraphQL also brings back each row's resolved overlays BESIDE the
 * rows, so we seed the overlay batch cache with them: a table that draws
 * attribution chips then skips its `POST /addresses/catalog/resolve` for that
 * page. The seed is written at the caller's configured default view scope, the
 * same tier the batch hook would have asked for — a seed at any other tier is
 * one nobody reads.
 *
 * The REST list needs no seed: its rows carry their own block (`row.catalog`),
 * and a table hands those to the batch hook as `carried` answers — see
 * {@link restAddressRowEntry}. That survives "Load more", live rows and a
 * narrowing search, which a cache entry keyed by one page's refs did not.
 */
export function useAddressesApi() {
  const client = useApiClient();
  const graphqlClient = useOptionalGraphqlClient();
  const flags = useFeatureFlags();
  const capabilities = useTransportCapabilities();
  const queryClient = useQueryClient();
  const { resolveDefaultResolveScope } = useScopePrefs();

  // `USER` IS the un-widened read, so it maps back to undefined — keeping the
  // request, and its cache key, identical to the plain default read. Mirrors
  // `useResolvedAddressOverlaysBatch`; the two disagreeing about the same
  // setting is what the seed would otherwise silently miss on.
  const effectiveScope = resolveDefaultResolveScope("address");
  const resolveScope = effectiveScope === "USER" ? undefined : effectiveScope;

  const seedOverlays = React.useCallback(
    (overlays: BatchOverlays, refs: OverlayRef[], scope?: AddressScope) => {
      // Seeded under BOTH `includeRow` keys, because the two are not the same
      // answer by contract: a batch fetched without it has empty row maps, and a
      // consumer that did not ask for rows must not start seeing them. The page
      // carried the rows either way, so the only cost is the empty-map variant.
      queryClient.setQueryData(addressOverlaysBatchQueryKey(refs, true, scope), overlays);
      queryClient.setQueryData(addressOverlaysBatchQueryKey(refs, false, scope), {
        ...overlays,
        rowByKey: new Map(),
        rowByAddress: new Map(),
      });
    },
    [queryClient],
  );

  return React.useMemo(
    () =>
      withGraphqlAddresses(createAddressesApi(client), {
        graphqlClient,
        flags,
        capabilities,
        seedOverlays,
        resolveScope,
      }),
    [client, graphqlClient, flags, capabilities, seedOverlays, resolveScope],
  );
}

/**
 * The overlay answer a REST address row CARRIES, as the batch read would index
 * it — or `null` when it carries none.
 *
 * The list handler resolves each row's leading catalog block with the same
 * builder `POST /addresses/catalog/resolve` uses, at the caller's own (`USER`)
 * view. A table passes these to `useResolvedAddressOverlaysBatch` as `carried`,
 * which then resolves only the refs no row answered.
 *
 * - The block is stripped off the row it rode in on, so the stored row reads as
 *   the row it is.
 * - `saved` defaults to true: a cross-chain lens row can be a live projection
 *   rather than a stored one, and says so; everything else a list returns is a
 *   registry row.
 * - A row WITHOUT a block is `null`, never an empty answer. A handler that did
 *   not resolve it has said nothing about the address, and indexing that as "no
 *   attribution" would blank its chips; `null` leaves the ref to be resolved.
 */
export function restAddressRowEntry(withBlock: Address): BatchEntry | null {
  const { catalog, ...row } = withBlock as Address & {
    catalog?: AddressCatalogBlock | null;
    saved?: boolean;
  };
  if (!catalog || typeof catalog !== "object") return null;
  return {
    address: row.address,
    chain: row.chain,
    overlays: catalogBlockToResolvedOverlays(catalog, { address: row.address, chainId: row.chain }),
    row: row as Address,
    saved: row.saved ?? true,
  };
}

/** {@link restAddressRowEntry} over several rows, dropping the ones that carry nothing. */
export function restAddressRowEntries(rows: readonly Address[]): BatchEntry[] {
  const entries: BatchEntry[] = [];
  for (const row of rows) {
    const entry = restAddressRowEntry(row);
    if (entry) entries.push(entry);
  }
  return entries;
}

/**
 * The `related-addresses-mgmt` port: address clusters, the evidenced links they
 * are concluded from, and the runs that produce them.
 *
 * Plain, unlike {@link useAddressesApi} — there is no GraphQL fast path and
 * nothing to seed, because a cluster read is one keyed lookup rather than a page
 * of rows a table would then re-resolve overlays for.
 */
export function useRelatedAddressesApi() {
  const client = useApiClient();
  return React.useMemo(() => createApiRelatedAddressesSource(client), [client]);
}

/**
 * The `teams-mgmt-api` port, with a display-name backfill on `listMembers`.
 *
 * A team member row is a snapshot written at seed/creation time; a tenant
 * bootstrapped by the identity seeder stamped the owner as the placeholder
 * "Seed Owner" (it never captured the SSO name). We resolve names from live
 * identity instead: the current user is authoritative from the session, and any
 * lingering "Seed Owner" on someone else degrades to their email local-part.
 * (The backend seeder now records the real name, so this is a transitional net
 * for already-seeded rows.)
 */
export function useTeamsApi(): TeamsApi {
  const client = useApiClient();
  const { session } = useSession();
  const selfId = session?.user.id ?? session?.claims.user_id ?? null;
  const selfName = session?.user.name ?? session?.names.userName ?? null;
  const selfEmail = session?.user.email ?? null;
  return React.useMemo<TeamsApi>(() => {
    const api = createApiTeamsApi(client);
    const orgUsers = createApiOrgUsersSource(client);
    /**
     * Org members' names by user id and by email, read only when a roster row
     * has no name of its own.
     *
     * A team member row carries a name only when something wrote one, and an
     * invite-accept writes just the email — so a teammate appeared as
     * "admin@authe.io" in the team roster directly under the organization list
     * that names them "Roel den Blanken". Best-effort: a failed read leaves the
     * roster exactly as the team API returned it.
     */
    const orgNames = async (): Promise<Map<string, string>> => {
      const names = new Map<string, string>();
      try {
        for (const person of await orgUsers.listMembers()) {
          const name = person.name?.trim();
          if (!name || name === person.email) continue;
          if (person.id) names.set(person.id, name);
          if (person.email) names.set(person.email.toLowerCase(), name);
        }
      } catch {
        // The roster still renders; it just keeps the emails.
      }
      return names;
    };
    const unnamed = (member: { name?: string; email?: string; id: string }): boolean => {
      const name = (member.name ?? "").trim();
      return !name || name === member.email || name === member.id;
    };
    type TeamMember = Awaited<ReturnType<typeof api.listMembers>>[number];
    const named = (member: TeamMember, names: Map<string, string>): TeamMember => {
      if (selfId && member.id === selfId) {
        return {
          ...member,
          name: selfName ?? member.name,
          email: member.email ?? selfEmail ?? undefined,
        };
      }
      if (unnamed(member)) {
        const known =
          names.get(member.id) ?? (member.email ? names.get(member.email.toLowerCase()) : undefined);
        if (known) return { ...member, name: known };
      }
      if ((member.name ?? "").trim().toLowerCase() === "seed owner") {
        const local = member.email ? member.email.split("@")[0] : "";
        return { ...member, name: local || member.email || member.name };
      }
      return member;
    };
    // One member back from a write. The store REPLACES the roster row with it,
    // so it needs the same naming as the list: without it, changing someone's
    // roles or status turned "Roel den blanken" into their email (and the avatar
    // initials with it) until the next reload.
    const namedOne = async (member: TeamMember): Promise<TeamMember> =>
      named(member, member.id !== selfId && unnamed(member) ? await orgNames() : new Map());
    return {
      ...api,
      async listMembers(team: Parameters<typeof api.listMembers>[0]) {
        const members = await api.listMembers(team);
        const needsNames = members.some((member) => member.id !== selfId && unnamed(member));
        const names = needsNames ? await orgNames() : new Map<string, string>();
        return members.map((member) => named(member, names));
      },
      async updateMemberRoles(...args: Parameters<typeof api.updateMemberRoles>) {
        return namedOne(await api.updateMemberRoles(...args));
      },
      async setMemberStatus(...args: Parameters<typeof api.setMemberStatus>) {
        return namedOne(await api.setMemberStatus(...args));
      },
    };
  }, [client, selfId, selfName, selfEmail]);
}

/**
 * The `trigger-mgmt-api` alerts port (trigger rules + destinations + fired
 * history). Crosses the camelCase-`AlertRule` ⇆ snake_case-`TriggerRuleRow`
 * boundary and uses POST-create / PATCH-update against
 * `/v0/{triggers,destinations,alerts}` (mapped from canonical `/alert-*` by
 * `RESOURCE_REWRITES`) — see `./alertsSource`.
 */
export function useAlertsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiPipelineAlertsSource(client), [client]);
}

/**
 * The `integrations-mgmt-api` port — the tenancy's customer-authored code.
 *
 * Reads the catalogue and its build records, and performs the one write this
 * app makes: promoting a built version to live. Speaks canonical
 * `/integrations` + `/integration-versions` paths, mapped onto
 * `private/integrations-mgmt-api/v0/*` by `RESOURCE_REWRITES`.
 */
export function useIntegrationsSource() {
  const client = useApiClient();
  return React.useMemo(() => createIntegrationsSource(client), [client]);
}

export function useSearchProvider() {
  const client = useApiClient();
  return React.useMemo(() => createApiSearchProvider(client), [client]);
}

/**
 * The `metadata-registry-api` saved-dashboards port (Datadog-style analytics
 * documents CRUD, resolved across the tenancy ladder). Speaks canonical
 * `/metadata-dashboards*` paths (mapped to `internal/metadata-registry-api` by
 * `RESOURCE_REWRITES`) and parses each blob through the shared
 * `@authe/fe-commons/metadata-analytics` schema — see `./analyticsSource`.
 * Per-panel data fetching is owned by `@authe/analytics-widgets`, not this port.
 */
export function useAnalyticsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiAnalyticsSource(client), [client]);
}

/**
 * The transaction-explorer WRITE port — promote a browsed field to a facet,
 * assemble facets into a view, and materialize authored views into physical
 * `mv_*` relations. The explorer READS via `@authe/analytics-widgets` hooks
 * over the same client; this port only carries the mutations (see
 * `./explorerSource`).
 */
export function useExplorerSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiExplorerSource(client), [client]);
}

/**
 * The protocol-definition port — read the decoding catalogue as a rung resolves
 * it, and author / drop that rung's `definition_patch`. The write counterpart of
 * the `definition_scope` marker the explorer puts on a decoded fact (see
 * `./protocolsSource`).
 */
export function useProtocolsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiProtocolsSource(client), [client]);
}

/**
 * The customer feature-flags port — the signed-in tenancy's resolved flags from
 * management-api `GET /features`. Read once per tenancy by
 * `app/DashboardFeatureFlags`, which feeds the flag store; components read a
 * flag with `useFeatureFlag(name)`, never this port (see `./featuresSource`).
 */
export function useFeaturesSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiFeaturesSource(client), [client]);
}

/**
 * The enforced-policy port — the signed-in tenancy's visibility policy from
 * management-api `GET /policies`. Read once per tenancy by `app/enforcedPolicy`,
 * which narrows the policy table every surface reads; components never read
 * this port (see `./policiesSource`).
 */
export function usePoliciesSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiPoliciesSource(client), [client]);
}

/**
 * The confidence-policy port — read the whole ladder (every rung + provenance)
 * and author this scope's slice of it. The sibling of `useProtocolsSource`: that
 * one tunes HOW a protocol decodes, this one tunes how sure we have to be before
 * we act on what it decoded.
 */
export function useEvidencePolicySource() {
  const client = useApiClient();
  return React.useMemo(() => createApiEvidencePolicySource(client), [client]);
}

/**
 * Notification inbox source. History + the read/archive mutations come from the
 * `notification_history` REST resource; live push is layered on via
 * `@authe/streaming` whenever a stream client is mounted (Live mode on). We
 * subscribe to the caller's customer/org/tenant, user, and team channels —
 * matching the publisher's audience fan-out — and
 * feed each event into the inbox via `mergeNotificationSources`. With no stream
 * client (Live off) this degrades to history-only, untouched.
 * Team channels come from the live membership read.
 */
export function useNotificationSource() {
  const client = useApiClient();
  const stream = useOptionalStreamingClient();
  const { session } = useSession();

  const customerId = session?.tenant.customerId ?? null;
  const teamIds = useTeamIds();
  const teamKey = JSON.stringify(teamIds);
  const orgId = session?.tenant.orgId ?? null;
  const tenantId = session?.tenant.tenantId ?? null;
  const userId = session?.user.id ?? session?.claims.user_id ?? null;

  return React.useMemo(() => {
    const base = createApiNotificationSource(client);
    if (!stream || (!customerId && !orgId && !tenantId && !userId)) return base;

    const channels = streamChannelIdsForCaller({ customerId, orgId, tenantId, userId }, STREAM_NOTIFICATIONS, JSON.parse(teamKey) as string[]);
    const live = {
      subscribe(onEvent: (event: AutheEvent) => void) {
        const unsubs = channels.map((id) =>
          stream.subscribeChannel(subscribeDescriptor(id), (message) => {
            const event = toNotificationEvent(message.payload);
            if (event) onEvent(event);
          }),
        );
        return () => {
          for (const off of unsubs) off();
        };
      },
    };
    return mergeNotificationSources(base, live);
  }, [client, stream, customerId, orgId, tenantId, userId, teamKey]);
}

/**
 * Presenter context for the notification inbox (bell + Activity page) built from
 * the live session. Lets the shared `describeEvent` render the signed-in user's
 * *own* events as "you" / "Signed in as <name>" (using the JWT `name`) instead
 * of the opaque `google-oauth2|…` principal, and tag the org/tenant they're in.
 * Other principals stay anonymised — the JWT only names the current user.
 */
export function useNotificationPresenter(): EventPresenterContext {
  const { session } = useSession();
  return React.useMemo<EventPresenterContext>(() => {
    const currentUserId = session?.user.id ?? null;
    const currentUserName = session?.user.name ?? session?.user.email ?? null;
    // The tenancy resolvers exist on `EventPresenterContext` and nothing in the
    // app passed them, so `eventScopeLabel` took its `?? orgId` fallback on
    // every row: each notification in the bell and on the Activity page was
    // subtitled `Workspace org_41403f9e2d1a4ed69c474b19f72779f7 ·
    // ten_5ffee9934e534ac3be0f1368d3570309`, on the same screen as a switcher
    // reading "test org / Production". The names were in the session the whole
    // time — this doc comment already promised to "tag the org/tenant they're
    // in", and only the ids were reaching the presenter.
    //
    // Scoped by id like `resolveActorName` above, and for the same reason: the
    // JWT names the CURRENT tenancy, so a name is only correct for the org and
    // tenant the reader is signed into. An event from elsewhere resolves to
    // `null` and keeps the shared fallback rather than borrowing this
    // workspace's name, which would be a worse error than an id.
    const orgId = session?.tenant.orgId ?? null;
    const tenantId = session?.tenant.tenantId ?? null;
    const orgName = session?.names.orgName ?? null;
    const tenantName = session?.names.tenantName ?? null;
    return {
      currentUserId,
      resolveActorName: (actorId) =>
        currentUserId && actorId === currentUserId ? currentUserName : null,
      resolveOrgLabel: (id) => (orgId && id === orgId ? orgName : null),
      resolveTenantLabel: (id) => (tenantId && id === tenantId ? tenantName : null),
    };
  }, [session]);
}

export function useSavedViewsSource() {
  const client = useApiClient();
  // The source needs the caller's id so `setDefault` only clears defaults on
  // rows this user owns — a tenant-shared view in that list can be somebody
  // else's, and the backend rightly refuses to mutate it. Read through the
  // shared accessor so this and dashboardPrefs cannot drift apart.
  const currentUserId = useCurrentUserId();
  return React.useMemo(
    () => createApiSavedViewsSource(client, { currentUserId }),
    [client, currentUserId],
  );
}

/**
 * The export ledger, backed by `saved-views-api`'s `/exports` routes.
 *
 * An export is the moment tenant data leaves the platform. Every `<ExportMenu>`
 * under `<DashboardExportLedger>` records one row per export — who, what, from
 * which view, how many rows, under which filters — and a failed export records
 * one too, because "no row appeared" is not an answer an operator can act on.
 *
 * The download never depends on this: `record` swallows its own errors (see
 * `createApiExportLedger`), so an unreachable ledger costs an audit row, never
 * the operator's file.
 */
export function useExportLedgerSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiExportLedger(client), [client]);
}

/**
 * The investigations index port, backed by `investigation-mgmt-api`.
 *
 * Cases were saved views at `tableId: "investigations"` until they outgrew that:
 * a saved view has no by-id read (so opening one case listed every case in the
 * tenant), it is one 400 KB item (so evidence, annotations, comments and workflow
 * had nowhere to grow), and it authorized under `shared-graphs` (so "rename a
 * saved graph" and "publish this annotation tenant-wide" were the same grant).
 *
 * The collaborative half — notes, discussion, the co-edited workflow — is a Yjs
 * room, and that room is now persisted too: `/investigations/:id/collab` hydrates
 * a joiner and accepts published updates, so a case survives the last browser tab
 * closing. See `./investigationsSource`.
 */
export function useInvestigationsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiInvestigationsSource(client), [client]);
}

/**
 * The `management-api` tenancy port (organizations + the tenants under the
 * active org). Backs the Settings "Organizations & tenants" section and the
 * top-bar tenant switcher's friendly names. Speaks canonical `/organizations/*`
 * paths (rewritten onto the `.management.` zone by `RESOURCE_REWRITES`) — see
 * `./tenancySource`.
 */
export function useTenancySource() {
  const client = useApiClient();
  return React.useMemo(() => createApiTenancySource(client), [client]);
}

/**
 * The `permissions-mgmt-api` org-user port (organization members + org role
 * catalog + invite). Backs the Team page's "Organization members" list and the
 * "Invite people" dialog. Speaks the dashboard-local canonical prefixes
 * `/org-users[/invite]` + `/org-roles` (rewritten onto the `.management.`
 * permissions zone in `app/api-client.ts`) — see `./orgUsersSource`.
 */
export function useOrgUsersSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiOrgUsersSource(client), [client]);
}

/**
 * The `pipeline-mgmt-api` port for the pipeline builder (catalog + definitions
 * CRUD + server-side validate/dry-run). Crosses the camelCase-builder ⇆
 * snake_case-wire boundary via the `@authe/fe-commons/pipeline` mappers; speaks
 * canonical `/pipelines/*` paths (mapped to `private/pipeline-mgmt-api` by
 * `RESOURCE_REWRITES`).
 */
export function usePipelinesSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiPipelinesSource(client), [client]);
}

/**
 * The `pipeline-mgmt-api` tracers port (flow-tracking definitions + their run
 * snapshots). Each run embeds an `InteractionGraph` that `@authe/tx-graph`
 * renders; this adapter crosses the camelCase ⇆ snake_case boundary and speaks
 * canonical `/tracers/*` paths (mapped to `private/pipeline-mgmt-api` by
 * `RESOURCE_REWRITES`).
 */
export function useTracersSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiTracersSource(client), [client]);
}

/**
 * The `pipeline-mgmt-api` run-jobs port (async forward-ingest + fan-out). Submits
 * a batch ingest (fixed payloads / latest-N / tx hashes), lists prior jobs, and
 * polls one job's status + per-tx outcomes so the run console can follow a
 * long-running ingest across a refresh. Speaks canonical `/runs/*` paths (mapped
 * to `private/pipeline-mgmt-api` by `RESOURCE_REWRITES`) — see `./runsSource`.
 */
export function useRunsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiRunsSource(client), [client]);
}

/**
 * The `seed-mgmt-api` human review queue port — the one place a quarantined
 * discovery becomes a catalog row, or does not.
 *
 * Everything the platform discovers is quarantined rather than written: the
 * pipeline's catalog flush and the transaction ingest's discovery both enqueue
 * here under the default `QUARANTINE` policy. List by status, read one with its
 * payload + diff + evidence, approve (optionally with edits) / reject / reopen,
 * and decide up to 100 in one call. Speaks canonical `/seed-reviews` paths,
 * mapped to `internal/seed-mgmt-api` by `RESOURCE_REWRITES` — see
 * `./reviewsSource`.
 */
export function useReviewsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiReviewsSource(client), [client]);
}

/**
 * The metering read port (`management-api` `GET /usage`). Backs every surface
 * that reports what was actually consumed: the billing page's live figures
 * against the plan's caps, the run console's per-run panel
 * (`?pipeline_run_id=`), and the activity ledger.
 *
 * The adapter itself lives in `@authe/fe-commons/plans` rather than in
 * `src/api/`, because the admin portal reads the same endpoint for its
 * "who used what" breakdown and neither app should own the wire shape of a
 * number a customer is billed from.
 *
 * The caller is never a parameter — `customer_id` comes from the verified auth
 * context server-side.
 */
export function useUsageApi(): UsageApi {
  const client = useApiClient();
  return React.useMemo(() => createUsageApi(client), [client]);
}

/**
 * The audit read port (`management-api` `GET /audit`) — the tenancy's raw call
 * trail, behind Activity → Audit.
 *
 * The other half of the Activity page: the notification inbox carries the
 * events a service chose to ANNOUNCE, this carries every invocation the
 * platform recorded. Dashboard-local rather than shared, because it answers one
 * tenant's question about its own workspace — the admin portal's equivalent
 * reads across customers and is a different surface.
 *
 * The tenancy is never a parameter: it comes from the verified auth context
 * server-side, so this port cannot be pointed at another workspace.
 */
export function useAuditSource(): AuditSource {
  const client = useApiClient();
  return React.useMemo(() => createAuditSource(client), [client]);
}

/**
 * The `pipeline-mgmt-api` replay port. Backs "re-check this group for the
 * missing leg" — a forced replay of a timed-out group's legs, queued and drained
 * by the replay worker. Speaks canonical `/replays` (mapped to
 * `private/pipeline-mgmt-api`) — see `./replaysSource`.
 */
export function useReplaysSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiReplaysSource(client), [client]);
}

/**
 * The `pipeline-mgmt-api` grouping-rules port — authoring the `tx_grouping`
 * engine's relationship-spec overrides across the scope ladder. Speaks canonical
 * `/grouping/rules` — see `@authe/fe-commons/grouping-rules` (shared with the
 * admin portal, which lists the platform's `system` rules through the same port).
 */
export function useGroupingRulesSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiGroupingRulesSource(client), [client]);
}

/**
 * The backend contract-state reader for the `@authe/contract-explorer`
 * "On-chain state" tab. POSTs to the `get-contract-state` lambda (canonical
 * `/abis/contract-state` → `/private/abis/v0/contract-state` via the shared
 * `ApiClient` routing) and returns the category-aware, proxy-merged state tree.
 * Without it that tab shows an unconfigured prompt (there is no RPC fallback).
 */
export function useContractStateClient() {
  const client = useApiClient();
  return React.useMemo(() => createApiContractStateClient(client), [client]);
}

/**
 * ABI resolver for the contract explorer. Composite: the caller's stored /
 * inherited / system-default ABIs from `abi-mgmt-api` (`GET /abis/{chain}/{address}`)
 * first, then the ERC20/721/1155 defaults as a fallback. Replaces the old
 * defaults-only provider so verified ABIs actually surface.
 */
export function useAbiProvider() {
  const client = useApiClient();
  return React.useMemo(
    () => createCompositeAbiProvider([createApiAbiProvider(client), createDefaultAbiProvider()]),
    [client],
  );
}

/**
 * Backend ABI writer (save + refresh) for the explorer's Edit tab. Permission-
 * gated on `abis:edit`/`abis:create`; the backend does copy-on-write when the
 * edited ABI is inherited/system-default, so a save never mutates the shared row.
 */
export function useAbiWriter() {
  const client = useApiClient();
  const { can } = usePermissions();
  // Two permissions, tracked apart. `save()` PATCHes first and falls back to
  // `POST /abis` when there is no row yet — and POST needs `abis:create`, which
  // `edit` does not imply. Folded into one boolean, a caller holding only
  // `abis:edit` got the Save button enabled and then a 404 followed by a 403 on
  // any contract they had not saved before.
  const canEdit = can(RESOURCES.abis, "edit");
  const canCreate = can(RESOURCES.abis, "create");
  return React.useMemo(
    () => createApiAbiWriter(client, { canEdit: canEdit || canCreate, canCreate }),
    [client, canEdit, canCreate],
  );
}

/**
 * Server-side proxy resolver for the explorer — `GET /proxy-overrides/{chain}/
 * {address}` on proxy-mgmt, which answers from the caller's override ladder first
 * and the shared point-in-time engine second. Needs `proxy:resolve`.
 */
export function useProxyResolver() {
  const client = useApiClient();
  return React.useMemo(() => createApiProxyResolver(client), [client]);
}

/**
 * The same resolution for a LIST of contracts, in ONE request — `POST
 * /proxy-overrides/resolve`.
 *
 * Use this wherever a surface holds several addresses. Proxy resolution gates
 * whatever read comes next (you cannot ask about an implementation until you
 * know which one it is), so resolving addresses one at a time spends a request
 * per row on the gate alone, and starting the dependent read speculatively
 * against the shell spends a metered read per row that turns out to be a proxy
 * — whose own data has no bearing on the answer. Both are avoidable; this is.
 *
 * Each entry carries a `chain` OR an `error`, never a fabricated non-proxy: the
 * backend reports a ref whose walk failed, or that it ran out of time for,
 * explicitly, and {@link useProxyResolver}'s "raise rather than degrade" rule
 * applies per ref here.
 */
export function useProxyBatchResolver() {
  const client = useApiClient();
  return React.useMemo(() => createApiProxyBatchResolver(client), [client]);
}

/**
 * Proxy override authoring (`proxy-mgmt`) for the explorer's Proxies tab: resolve
 * with provenance, the upgrade timeline, and the pin/unpin writes.
 *
 * Separate from {@link useProxyResolver}, which maps a resolved chain onto the
 * explorer's `ProxyChain` for display. This one keeps the wire row intact —
 * scope, evidence, block window — because that is exactly what an operator needs
 * in order to decide whether to override it.
 */
export function useProxyOverridesClient() {
  const client = useApiClient();
  return React.useMemo(() => createProxyOverridesClient(client), [client]);
}

/**
 * The three proxy authoring permissions, read once so the page and the panel
 * agree. `edit` pins an implementation for the caller's own rung; `delete` both
 * purges the SHARED discovery rows and removes the caller's own pins (one route,
 * split by `?purge=`); `index` queues a full upgrade-history build, which is an
 * unbounded log scan plus per-block on-chain re-detects.
 *
 * All three are read separately because proxy-mgmt checks them separately —
 * folding them together is how a reader was shown a "Build history" button that
 * could only ever 403.
 */
export function useProxyPermissions(): { canEdit: boolean; canPurge: boolean; canIndex: boolean } {
  const { can } = usePermissions();
  return React.useMemo(
    () => ({
      canEdit: can(RESOURCES.proxy, "edit"),
      canPurge: can(RESOURCES.proxy, "delete"),
      canIndex: can(RESOURCES.proxy, "index"),
    }),
    [can],
  );
}

/**
 * Lists every ABI visible for a contract (`GET /abis/by-address/{chain}/{address}`):
 * the caller's overrides across the scope ladder + the public catalog row.
 * Powers the explorer's "Sources" picker.
 */
export function useAbiByAddressClient() {
  const client = useApiClient();
  return React.useMemo(() => createApiAbiByAddressClient(client), [client]);
}

/**
 * Verified source code + (opt-in) deployed bytecode for the explorer's "Source"
 * tab (`GET /source/{chain}/{address}` → `/private/abis/v0/source/…`). The
 * backend fans out to third-party verifiers (Codeslaw / Etherscan / Blockscout /
 * Sourcify) and reconciles the best verified source.
 */
export function useContractSourceClient() {
  const client = useApiClient();
  return React.useMemo(() => createApiContractSourceClient(client), [client]);
}

export type {
  CatalogField,
  CatalogLocation,
  CatalogOption,
  CatalogScope,
  FlowCatalog,
  SpecCatalog,
  GroupingRuleSummary,
  GroupingRuleScope,
  RuleStanding,
  RuleStandingState,
  SaveGroupingRuleInput,
} from "@authe/fe-commons/grouping-rules";
